/**
 * OneDrive File Browser — custom Graph API-based file browser.
 * Microsoft's v8 Picker widget is blocked by their own CSP (iframe) and COOP
 * (popup) headers for third-party origins. Instead we use the Graph API
 * directly to list and select files.
 */

import { stateful, useState, useEffect } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import type { CloudFileEntry } from './cloud-picker-google.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DriveItem {
  id: string;
  name: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
  size?: number;
  lastModifiedDateTime?: string;
  parentReference?: { driveId?: string; path?: string };
}

export interface OneDrivePickerProps {
  accessToken: string;
  onFilesSelected: (entries: CloudFileEntry[]) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OneDrivePicker = stateful((props: OneDrivePickerProps) => {
  const { accessToken, onFilesSelected } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoOpened, setAutoOpened] = useState(false);

  // Auto-open file browser when accessToken is first set
  useEffect(() => {
    if (accessToken && !autoOpened) {
      setAutoOpened(true);
      openPicker();
    }
  }, [accessToken]);

  const openPicker = async () => {
    setLoading(true);
    setError(null);

    try {
      const items = await openFileBrowser(accessToken);

      if (!items || items.length === 0) {
        setLoading(false);
        return;
      }

      // Create entries in 'downloading' state
      const entries: CloudFileEntry[] = items.map(item => ({
        id: item.id,
        name: item.name,
        mimeType: item.file?.mimeType || 'application/octet-stream',
        source: 'onedrive' as const,
        status: 'downloading' as const,
      }));

      onFilesSelected(entries);

      // Download each file via server proxy
      const results: CloudFileEntry[] = [];
      for (const item of items) {
        try {
          const driveId = item.parentReference?.driveId;
          const res = await fetch('/api/onedrive/download', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              driveId: driveId || 'me',
              itemId: item.id,
              accessToken,
              fileName: item.name,
            }),
          });

          if (!res.ok) {
            const data = await res.json().catch(() => ({ error: 'Request failed' }));
            results.push({
              ...entries.find(e => e.id === item.id)!,
              status: 'error',
              error: data.error || `Server error (${res.status})`,
            });
            continue;
          }

          const data = (await res.json()) as {
            fileName: string;
            base64: string;
            mimeType: string;
          };

          const binary = atob(data.base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const file = new File([bytes], data.fileName || item.name, { type: data.mimeType });

          const { parseDocument } = await import('./document-parser.js');
          const parsed = await parseDocument(file);
          results.push({
            ...entries.find(e => e.id === item.id)!,
            status: 'done',
            parsed,
          });
        } catch (err) {
          results.push({
            ...entries.find(e => e.id === item.id)!,
            status: 'error',
            error: err instanceof Error ? err.message : 'Failed to download file',
          });
        }
      }

      onFilesSelected(results);
    } catch (err) {
      if (err instanceof Error && (err.message === 'cancelled' || err.message === 'Sign-in popup was closed')) {
        // silent
      } else {
        console.error('OneDrive error:', err);
        setError(err instanceof Error ? err.message : 'Failed to open OneDrive');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!accessToken) {
    return html`<span class="text-base-content/40 text-xs italic">No access token</span>`;
  }

  return html`
    <span class="inline-flex items-center gap-1">
      ${loading
        ? html`<span class="loading loading-spinner loading-xs"></span><span class="text-base-content/50 text-xs">Loading OneDrive…</span>`
        : nothing}
      ${error ? html`<span class="text-error text-xs">${error}</span>` : nothing}
    </span>
  `;
});

// ---------------------------------------------------------------------------
// Graph API helpers
// ---------------------------------------------------------------------------

async function graphGet<T>(accessToken: string, url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, any>).error?.message || `Graph API error (${res.status})`);
  }
  return res.json() as Promise<T>;
}

async function listChildren(accessToken: string, itemId?: string): Promise<DriveItem[]> {
  const path = itemId
    ? `https://graph.microsoft.com/v1.0/me/drive/items/${encodeURIComponent(itemId)}/children`
    : 'https://graph.microsoft.com/v1.0/me/drive/root/children';
  const data = await graphGet<{ value: DriveItem[] }>(accessToken, path + '?$top=200&$orderby=name');
  return data.value;
}

// ---------------------------------------------------------------------------
// Custom file browser modal
// ---------------------------------------------------------------------------

function openFileBrowser(accessToken: string): Promise<DriveItem[]> {
  return new Promise(resolve => {
    const selected = new Map<string, DriveItem>();
    let currentFolderId: string | undefined;
    let folderStack: { id: string | undefined; name: string }[] = [{ id: undefined, name: 'OneDrive' }];

    // ── Overlay ────────────────────────────────────────────────────────
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,0.5);' +
      'display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,sans-serif';

    const modal = document.createElement('div');
    modal.style.cssText =
      'background:white;border-radius:12px;width:90vw;max-width:720px;height:80vh;max-height:600px;' +
      'display:flex;flex-direction:column;box-shadow:0 25px 50px -12px rgba(0,0,0,0.5);overflow:hidden';
    overlay.appendChild(modal);

    // ── Header ─────────────────────────────────────────────────────────
    const header = document.createElement('div');
    header.style.cssText =
      'padding:16px 20px;border-bottom:1px solid #e5e7eb;display:flex;align-items:center;gap:12px;flex-shrink:0';
    header.innerHTML =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="#0078d4"><path d="M10.5 18H8.25C6.18 18 4.5 16.32 4.5 14.25C4.5 12.55 5.62 11.1 7.18 10.62C7.25 8.54 8.94 6.88 11.03 6.88C11.93 6.88 12.76 7.2 13.41 7.73C14.15 6.66 15.37 6 16.73 6C18.89 6 20.63 7.74 20.63 9.9C20.63 10.12 20.61 10.34 20.57 10.55C21.91 11.1 22.88 12.42 22.88 13.97C22.88 16 21.25 17.63 19.22 17.63H15.75" stroke="#0078d4" stroke-width="0" fill="#0078d4" opacity="0.2"/><path d="M14 13l-2.5 4L9 13" stroke="#0078d4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>' +
      '<span style="font-weight:600;font-size:16px;color:#1f2937">OneDrive</span>';
    modal.appendChild(header);

    // ── Breadcrumbs ────────────────────────────────────────────────────
    const breadcrumbBar = document.createElement('div');
    breadcrumbBar.style.cssText =
      'padding:8px 20px;border-bottom:1px solid #f3f4f6;display:flex;align-items:center;gap:4px;flex-shrink:0;' +
      'font-size:13px;color:#6b7280;overflow-x:auto;white-space:nowrap';
    modal.appendChild(breadcrumbBar);

    // ── File list container ────────────────────────────────────────────
    const listContainer = document.createElement('div');
    listContainer.style.cssText = 'flex:1;overflow-y:auto;padding:4px 0';
    modal.appendChild(listContainer);

    // ── Footer ─────────────────────────────────────────────────────────
    const footer = document.createElement('div');
    footer.style.cssText =
      'padding:12px 20px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;flex-shrink:0';

    const selectionInfo = document.createElement('span');
    selectionInfo.style.cssText = 'font-size:13px;color:#6b7280';

    const btnGroup = document.createElement('div');
    btnGroup.style.cssText = 'display:flex;gap:8px';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText =
      'padding:8px 16px;border:1px solid #d1d5db;border-radius:6px;background:white;cursor:pointer;font-size:14px;color:#374151';
    cancelBtn.onmouseover = () => (cancelBtn.style.background = '#f9fafb');
    cancelBtn.onmouseout = () => (cancelBtn.style.background = 'white');
    cancelBtn.onclick = () => { cleanup(); resolve([]); };

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = 'Select';
    confirmBtn.disabled = true;
    confirmBtn.style.cssText =
      'padding:8px 16px;border:none;border-radius:6px;background:#0078d4;color:white;cursor:pointer;font-size:14px;font-weight:500;opacity:0.5';
    confirmBtn.onclick = () => { cleanup(); resolve([...selected.values()]); };

    btnGroup.appendChild(cancelBtn);
    btnGroup.appendChild(confirmBtn);
    footer.appendChild(selectionInfo);
    footer.appendChild(btnGroup);
    modal.appendChild(footer);

    // ── Close handlers ─────────────────────────────────────────────────
    overlay.addEventListener('click', e => {
      if (e.target === overlay) { cleanup(); resolve([]); }
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { cleanup(); resolve([]); }
    };
    document.addEventListener('keydown', onKeyDown);

    function cleanup() {
      document.removeEventListener('keydown', onKeyDown);
      overlay.remove();
    }

    document.body.appendChild(overlay);

    // ── Spinner style ──────────────────────────────────────────────────
    if (!document.getElementById('od-spinner-style')) {
      const style = document.createElement('style');
      style.id = 'od-spinner-style';
      style.textContent = '@keyframes od-spin { to { transform: rotate(360deg) } }';
      document.head.appendChild(style);
    }

    // ── Update selection ───────────────────────────────────────────────
    function updateSelection() {
      const count = selected.size;
      selectionInfo.textContent = count === 0 ? '' : `${count} file${count > 1 ? 's' : ''} selected`;
      confirmBtn.disabled = count === 0;
      confirmBtn.style.opacity = count === 0 ? '0.5' : '1';
    }

    // ── Render breadcrumbs ─────────────────────────────────────────────
    function renderBreadcrumbs() {
      breadcrumbBar.innerHTML = '';
      folderStack.forEach((crumb, i) => {
        if (i > 0) {
          const sep = document.createElement('span');
          sep.textContent = ' › ';
          sep.style.color = '#9ca3af';
          breadcrumbBar.appendChild(sep);
        }
        const link = document.createElement('span');
        link.textContent = crumb.name;
        if (i < folderStack.length - 1) {
          link.style.cssText = 'cursor:pointer;color:#0078d4';
          link.onmouseover = () => (link.style.textDecoration = 'underline');
          link.onmouseout = () => (link.style.textDecoration = 'none');
          link.onclick = () => {
            folderStack = folderStack.slice(0, i + 1);
            currentFolderId = crumb.id;
            loadFolder();
          };
        } else {
          link.style.cssText = 'color:#1f2937;font-weight:500';
        }
        breadcrumbBar.appendChild(link);
      });
    }

    // ── Render items ───────────────────────────────────────────────────
    function renderItems(items: DriveItem[]) {
      listContainer.innerHTML = '';

      if (items.length === 0) {
        listContainer.innerHTML =
          '<div style="padding:40px;text-align:center;color:#9ca3af;font-size:14px">This folder is empty</div>';
        return;
      }

      // Folders first, then files
      const sorted = [...items].sort((a, b) => {
        if (a.folder && !b.folder) return -1;
        if (!a.folder && b.folder) return 1;
        return a.name.localeCompare(b.name);
      });

      for (const item of sorted) {
        const row = document.createElement('div');
        row.style.cssText =
          'display:flex;align-items:center;gap:12px;padding:8px 20px;cursor:pointer;transition:background 0.1s';
        row.onmouseover = () => (row.style.background = '#f9fafb');
        row.onmouseout = () => (row.style.background = selected.has(item.id) ? '#eff6ff' : 'transparent');

        if (item.folder) {
          const icon = document.createElement('span');
          icon.style.cssText = 'font-size:20px;flex-shrink:0';
          icon.textContent = '📁';
          row.appendChild(icon);

          const name = document.createElement('span');
          name.style.cssText = 'flex:1;font-size:14px;color:#1f2937;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
          name.textContent = item.name;
          row.appendChild(name);

          const count = document.createElement('span');
          count.style.cssText = 'font-size:12px;color:#9ca3af;flex-shrink:0';
          count.textContent = `${item.folder.childCount} items`;
          row.appendChild(count);

          const chevron = document.createElement('span');
          chevron.style.cssText = 'color:#9ca3af;font-size:14px;flex-shrink:0';
          chevron.textContent = '›';
          row.appendChild(chevron);

          row.onclick = () => {
            folderStack.push({ id: item.id, name: item.name });
            currentFolderId = item.id;
            loadFolder();
          };
        } else {
          // File row with checkbox
          const checkbox = document.createElement('input');
          checkbox.type = 'checkbox';
          checkbox.checked = selected.has(item.id);
          checkbox.style.cssText = 'width:16px;height:16px;flex-shrink:0;accent-color:#0078d4;cursor:pointer';
          checkbox.onclick = e => e.stopPropagation();
          checkbox.onchange = () => {
            if (checkbox.checked) {
              selected.set(item.id, item);
              row.style.background = '#eff6ff';
            } else {
              selected.delete(item.id);
              row.style.background = 'transparent';
            }
            updateSelection();
          };
          row.appendChild(checkbox);

          const icon = document.createElement('span');
          icon.style.cssText = 'font-size:20px;flex-shrink:0';
          icon.textContent = getFileIcon(item.name);
          row.appendChild(icon);

          const name = document.createElement('span');
          name.style.cssText = 'flex:1;font-size:14px;color:#1f2937;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
          name.textContent = item.name;
          row.appendChild(name);

          if (item.size) {
            const size = document.createElement('span');
            size.style.cssText = 'font-size:12px;color:#9ca3af;flex-shrink:0';
            size.textContent = formatSize(item.size);
            row.appendChild(size);
          }

          if (selected.has(item.id)) {
            row.style.background = '#eff6ff';
          }

          row.onclick = () => {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change'));
          };
        }

        listContainer.appendChild(row);
      }
    }

    // ── Load folder ────────────────────────────────────────────────────
    async function loadFolder() {
      listContainer.innerHTML =
        '<div style="padding:40px;text-align:center"><div style="display:inline-block;width:24px;height:24px;' +
        'border:3px solid #e5e7eb;border-top-color:#0078d4;border-radius:50%;animation:od-spin 0.6s linear infinite"></div></div>';
      renderBreadcrumbs();

      try {
        const items = await listChildren(accessToken, currentFolderId);
        renderItems(items);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        listContainer.innerHTML =
          `<div style="padding:40px;text-align:center;color:#ef4444;font-size:14px">Failed to load files: ${escapeHtml(msg)}</div>`;
      }
    }

    // Start
    loadFolder();
  });
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'pdf': return '📕';
    case 'doc': case 'docx': return '📘';
    case 'xls': case 'xlsx': return '📗';
    case 'ppt': case 'pptx': return '📙';
    case 'txt': return '📄';
    case 'jpg': case 'jpeg': case 'png': case 'gif': case 'svg': case 'webp': return '🖼️';
    case 'mp4': case 'mov': case 'avi': return '🎬';
    case 'mp3': case 'wav': return '🎵';
    case 'zip': case 'rar': case '7z': return '📦';
    default: return '📄';
  }
}
